/**
 * Navigation Builder Utility
 * 
 * Builds sidebar menu from EspoCRM metadata navigation with ACL filtering.
 * This is a generic utility that can be used across all tenants.
 */

import { NavigationMenuItem, UserInfoWithAcl, EspoCrmMetadata, TabListItem } from '../types/espoCrm';
import { MenuItems, MenuItem } from 'src/core/types/appConfig';
import { isScopeVisible } from '../services/aclService';
import { getNavigationIcon } from './iconMapper';

/**
 * Translate entity name using translations object
 */
function translateEntityNameSync(
  entityName: string,
  translations: Record<string, any> | null | undefined
): string {
  if (!translations) {
    return entityName;
  }

  const possibleKeys = [
    `Global.scopeNames.${entityName}`,
    `Global.scopeNamesPlural.${entityName}`,
    `scopeNames.${entityName}`,
    `scopeNamesPlural.${entityName}`,
    `Entity.${entityName}`,
    `Entities.${entityName}`,
  ];

  for (const key of possibleKeys) {
    const value = getNestedValue(translations, key);
    if (value && typeof value === 'string') {
      return value;
    }
  }

  return entityName;
}

/**
 * Get nested value from object using dot notation
 */
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
}

/**
 * Convert EspoCRM navigation menu item to framework MenuItem
 */
function convertMenuItem(
  navItem: NavigationMenuItem,
  userInfo: UserInfoWithAcl | null | undefined,
  translations?: Record<string, any> | null
): MenuItem | null {
  // If item has a scope, check if user has read permission
  if (navItem.scope && !isScopeVisible(userInfo, navItem.scope)) {
    return null;
  }

  // Translate label/name if scope is available
  let displayName = navItem.label || navItem.name || '';
  if (navItem.scope && translations) {
    const translatedScope = translateEntityNameSync(navItem.scope, translations);
    // If we have a label, keep it, otherwise use translated scope name
    if (!navItem.label) {
      displayName = translatedScope;
    }
  }

  // Build menu item
  const menuItem: MenuItem = {
    name: displayName,
    link: navItem.url || navItem.link || (navItem.scope ? `/${navItem.scope}` : undefined),
  };

  // Map icon using entity name or icon class
  if (navItem.scope || navItem.iconClass) {
    const IconComponent = getNavigationIcon(navItem.scope, navItem.iconClass);
    menuItem.icon = IconComponent;
  }

  // Add badge if available
  if (navItem.badge) {
    menuItem.badge = navItem.badge;
  }

  // Process nested items
  if (navItem.items && navItem.items.length > 0) {
    const nestedItems = navItem.items
      .map((item) => convertMenuItem(item, userInfo, translations))
      .filter((item): item is MenuItem => item !== null);

    if (nestedItems.length > 0) {
      menuItem.items = nestedItems;
    }
  }

  return menuItem;
}

/**
 * Build menu items from EspoCRM navigation metadata
 */
export function buildMenuItems(
  metadata: EspoCrmMetadata,
  userInfo: UserInfoWithAcl | null | undefined,
  translations?: Record<string, any> | null
): MenuItems[] {
  // Get navigation from metadata.navigation.menu or metadata.client.menu
  const navigation =
    metadata.navigation?.menu || metadata.client?.menu || [];

  if (!Array.isArray(navigation) || navigation.length === 0) {
    return [];
  }

  // Group menu items by their structure
  // EspoCRM navigation can have groups or flat structure
  const menuGroups: MenuItems[] = [];

  // Process each top-level menu item
  for (const navItem of navigation) {
    const menuItem = convertMenuItem(navItem, userInfo, translations);

    if (menuItem) {
      // Check if this should be a group or single item
      if (menuItem.items && menuItem.items.length > 0) {
        // It's a group
        menuGroups.push({
          heading: menuItem.name,
          items: menuItem.items,
        });
      } else {
        // Single item - add to a default group or create a new one
        if (menuGroups.length === 0 || menuGroups[menuGroups.length - 1].heading !== 'Menu') {
          menuGroups.push({
            heading: 'Menu',
            items: [],
          });
        }
        menuGroups[menuGroups.length - 1].items.push(menuItem);
      }
    }
  }

  return menuGroups;
}

/**
 * Build menu items from entity scopes (fallback if navigation is not available)
 */
export function buildMenuItemsFromScopes(
  metadata: EspoCrmMetadata,
  userInfo: UserInfoWithAcl | null | undefined,
  translations?: Record<string, any> | null
): MenuItems[] {
  const scopes = metadata.scopes || {};
  const visibleScopes = Object.keys(scopes).filter((scope) =>
    isScopeVisible(userInfo, scope)
  );

  if (visibleScopes.length === 0) {
    return [];
  }

  // Group by scope type or create a flat list
  const menuItems: MenuItem[] = visibleScopes
    .map((scope) => {
      const scopeDef = scopes[scope];
      if (!scopeDef || !scopeDef.tab) {
        return null;
      }

      // Get icon for the scope
      const IconComponent = getNavigationIcon(scope, scopeDef.iconClass);

      // Translate scope name
      const translatedName = translations 
        ? translateEntityNameSync(scope, translations)
        : (scopeDef.label || scope);

      return {
        name: translatedName,
        link: `/${scope}`,
        icon: IconComponent,
      } as MenuItem;
    })
    .filter((item): item is MenuItem => item !== null && item.link !== undefined);

  if (menuItems.length === 0) {
    return [];
  }

  return [
    {
      heading: 'Entities',
      items: menuItems,
    },
  ];
}

/**
 * Build menu items from settings.tabList (primary method)
 * This is the preferred method as it respects user's tab configuration
 */
export function buildMenuFromTabList(
  metadata: EspoCrmMetadata,
  userInfo: UserInfoWithAcl | null | undefined,
  translations?: Record<string, any> | null
): MenuItems[] {
  // Get tabList from userInfo.settings.tabList
  const tabList = userInfo?.settings?.tabList;
  
  console.log('buildMenuFromTabList - Input:', {
    hasUserInfo: !!userInfo,
    hasSettings: !!userInfo?.settings,
    hasTabList: !!tabList,
    tabListLength: tabList?.length || 0,
    tabList: tabList,
    hasMetadata: !!metadata,
    hasScopes: !!metadata?.scopes,
    scopesCount: Object.keys(metadata?.scopes || {}).length,
  });
  
  if (!Array.isArray(tabList) || tabList.length === 0) {
    console.log('buildMenuFromTabList - No tabList found, returning empty array');
    return [];
  }

  const scopes = metadata.scopes || {};
  const menuGroups: MenuItems[] = [];
  let currentGroup: MenuItems | null = null;

  for (const item of tabList) {
    // Handle divider objects
    if (typeof item === 'object' && item.type === 'divider') {
      // If we have a current group with items, finalize it
      if (currentGroup && currentGroup.items.length > 0) {
        menuGroups.push(currentGroup);
      }
      
      // Start a new group with the divider text as heading
      // Remove $ prefix if present (EspoCRM uses $ for translation keys)
      const heading = item.text?.replace(/^\$/, '') || 'Menu';
      currentGroup = {
        heading,
        items: [],
      };
      continue;
    }

    // Handle delimiter string
    if (item === '_delimiter_') {
      // If we have a current group with items, finalize it
      if (currentGroup && currentGroup.items.length > 0) {
        menuGroups.push(currentGroup);
      }
      currentGroup = null;
      continue;
    }

    // Handle entity name strings
    if (typeof item === 'string') {
      const entityName = item;
      
      // Check if entity exists in scopes
      const scopeDef = scopes[entityName];
      if (!scopeDef) {
        continue; // Skip if entity not found in metadata
      }

      // Check ACL permission
      if (!isScopeVisible(userInfo, entityName)) {
        continue; // Skip if user doesn't have read permission
      }

      // Check if entity should be shown in tab
      if (scopeDef.tab === false) {
        continue; // Skip if tab is explicitly disabled
      }

      // Get icon for the entity
      const IconComponent = getNavigationIcon(entityName, scopeDef.iconClass);

      // Get label from scope definition or use entity name, with translation
      const label = translations 
        ? translateEntityNameSync(entityName, translations)
        : (scopeDef.label || entityName);

      // Create menu item
      const menuItem: MenuItem = {
        name: label,
        link: `/${entityName}`,
        icon: IconComponent,
      };

      // Add to current group or create a default group
      if (!currentGroup) {
        currentGroup = {
          heading: 'Menu',
          items: [],
        };
      }
      currentGroup.items.push(menuItem);
    }
  }

  // Add the last group if it has items
  if (currentGroup && currentGroup.items.length > 0) {
    menuGroups.push(currentGroup);
  }

  console.log('buildMenuFromTabList - Output:', {
    menuGroupsCount: menuGroups.length,
    menuGroups: menuGroups.map(g => ({
      heading: g.heading,
      itemsCount: g.items.length,
      items: g.items.map(i => ({ name: i.name, link: i.link })),
    })),
  });

  return menuGroups;
}

/**
 * Build complete menu structure
 * Tries tabList first (from user settings), then navigation, then scopes
 */
export function buildMenu(
  metadata: EspoCrmMetadata,
  userInfo: UserInfoWithAcl | null | undefined,
  translations?: Record<string, any> | null
): MenuItems[] {
  // Try to build from tabList (preferred method)
  const tabListMenu = buildMenuFromTabList(metadata, userInfo, translations);
  if (tabListMenu.length > 0) {
    return tabListMenu;
  }

  // Try to build from navigation
  const navMenu = buildMenuItems(metadata, userInfo, translations);
  if (navMenu.length > 0) {
    return navMenu;
  }

  // Fallback to scopes
  return buildMenuItemsFromScopes(metadata, userInfo, translations);
}

